import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Check, Loader2, XCircle, Sparkles, ArrowRight, Heart, Brain, Users, Zap, Star, Leaf } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { token, isAuthenticated, refreshUser } = useAuth();
    const [status, setStatus] = useState('loading');
    const [tier, setTier] = useState(null);
    const [pollAttempts, setPollAttempts] = useState(0);

    const sessionId = searchParams.get('session_id');
    const maxPollAttempts = 10;
    const pollInterval = 2000;

    // Grounding quotes for the success state
    const groundingQuotes = [
        {
            quote: "You just did something brave — you chose yourself.",
            author: "Your journey to calm starts now"
        },
        {
            quote: "Healing isn't linear, but showing up for yourself is always a step forward.",
            author: "Welcome to your support system"
        },
        {
            quote: "You deserve tools that meet you where you are.",
            author: "We're glad you're here"
        },
        {
            quote: "Regulation isn't about being perfect. It's about having support when you need it.",
            author: "This is your safe space"
        },
        {
            quote: "Taking care of your nervous system is one of the most loving things you can do.",
            author: "You've taken the first step"
        }
    ];
    
    const [selectedQuote] = useState(() => 
        groundingQuotes[Math.floor(Math.random() * groundingQuotes.length)]
    );

    const checkPaymentStatus = useCallback(async (attempts = 0) => {
        if (!sessionId || !token) {
            setStatus('error');
            return;
        }

        try {
            const response = await axios.get(`${API}/api/stripe/checkout-status/${sessionId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const { payment_status, status: sessionStatus, tier: upgradedTier } = response.data;

            if (payment_status === 'paid') {
                setStatus('success');
                setTier(upgradedTier);
                // Refresh user data to get updated subscription
                if (refreshUser) refreshUser();
                return;
            }

            if (sessionStatus === 'expired') {
                setStatus('expired');
                return;
            }

            if (attempts < maxPollAttempts) {
                setPollAttempts(attempts + 1);
                setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
            } else {
                setStatus('success');
                setTier(upgradedTier);
            }
        } catch (error) {
            console.error('Payment status check error:', error);
            if (attempts < maxPollAttempts) {
                setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
            } else {
                setStatus('error');
            }
        }
    }, [sessionId, token, refreshUser]);

    useEffect(() => {
        if (!sessionId) {
            setStatus('error');
            return;
        }

        if (!isAuthenticated) {
            navigate('/auth?redirect=/payment/success?session_id=' + sessionId);
            return;
        }

        checkPaymentStatus(0);
    }, [sessionId, isAuthenticated, navigate, checkPaymentStatus]);

    const tierInfo = {
        essentials: {
            name: 'Essentials',
            icon: Zap,
            color: '#1D6B6B',
            bgColor: 'bg-[#1D6B6B]/10',
            description: 'You now have access to the full grounding toolkit',
            features: [
                'All 10+ breathing patterns',
                'Micro-reset quick tool',
                'Body scan meditation',
                'Progress tracking',
                'Save your favourite tools'
            ],
            nextStep: 'Try the Micro-Reset tool for a quick regulation exercise',
            nextAction: '/regulate'
        },
        support_plus: {
            name: 'Support+',
            icon: Brain,
            color: '#1D6B6B',
            bgColor: 'bg-[#1D6B6B]/10',
            description: 'Welcome to personalized AI-powered support',
            features: [
                'Everything in Essentials',
                'AI Guide companion',
                'Guided meditations library',
                'Self-talk reframe tools',
                'Pause & Repair scripts'
            ],
            nextStep: 'Meet your AI Guide for personalized support',
            nextAction: '/guide'
        },
        partner: {
            name: 'Partner',
            icon: Users,
            color: '#E08850',
            bgColor: 'bg-[#E08850]/10',
            description: 'Ready to co-regulate together',
            features: [
                'Everything in Support+',
                'Partner co-regulation mode',
                'Shared grounding sessions',
                'Communication scripts',
                'Relationship templates'
            ],
            nextStep: 'Set up Partner Mode to start co-regulating',
            nextAction: '/partner'
        },
        // Legacy tiers
        support: {
            name: 'Support+',
            icon: Brain,
            color: '#1D6B6B',
            bgColor: 'bg-[#1D6B6B]/10',
            description: 'Welcome to personalized AI-powered support',
            features: [
                'AI Guide companion',
                'Guided meditations',
                'Self-talk reframes',
                'Full grounding library'
            ],
            nextStep: 'Meet your AI Guide',
            nextAction: '/guide'
        },
        deep_support: {
            name: 'Partner',
            icon: Users,
            color: '#E08850',
            bgColor: 'bg-[#E08850]/10',
            description: 'Ready to co-regulate together',
            features: [
                'Partner co-regulation mode',
                'Shared sessions',
                'Communication scripts'
            ],
            nextStep: 'Set up Partner Mode',
            nextAction: '/partner'
        }
    };

    const currentTier = tierInfo[tier] || tierInfo.support_plus;
    const TierIcon = currentTier.icon;

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex flex-col">
            {/* Header - with mobile menu */}
            <PublicHeader />

            <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12 pt-24">
                <div className="max-w-lg w-full">
                    
                    {/* Loading State */}
                    {status === 'loading' && (
                        <div className="text-center animate-fade-in">
                            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center">
                                <Loader2 className="w-10 h-10 text-[#1D6B6B] animate-spin" />
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-[#1D3D3D] mb-3">
                                Processing your payment...
                            </h1>
                            <p className="text-[#5A7A7A] mb-6">
                                Please wait while we activate your subscription.
                            </p>
                            <div className="flex items-center justify-center gap-2 text-sm text-[#7D8E95]">
                                <div className="flex gap-1">
                                    {[...Array(maxPollAttempts)].map((_, i) => (
                                        <div 
                                            key={i} 
                                            className={`w-2 h-2 rounded-full transition-colors ${
                                                i <= pollAttempts ? 'bg-[#1D6B6B]' : 'bg-[#E8E1D6]'
                                            }`} 
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Success State */}
                    {status === 'success' && (
                        <div className="animate-fade-in">
                            {/* Grounding Quote Header */}
                            <div className="text-center mb-10">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#1D6B6B]/20 to-[#2A8585]/10 mb-6">
                                    <Leaf className="w-8 h-8 text-[#1D6B6B]" />
                                </div>
                                <blockquote className="mb-4">
                                    <p className="text-2xl sm:text-3xl font-medium text-[#1D3D3D] leading-relaxed italic">
                                        "{selectedQuote.quote}"
                                    </p>
                                </blockquote>
                                <p className="text-[#5A7A7A] font-medium">
                                    — {selectedQuote.author}
                                </p>
                            </div>

                            {/* Plan Confirmation Card */}
                            <div className="bg-white rounded-2xl shadow-lg border border-[#E8E1D6] overflow-hidden mb-6">
                                {/* Card Header */}
                                <div className="px-6 py-4 bg-gradient-to-r from-[#FEF6EF] to-[#FDF8F4] border-b border-[#E8E1D6]">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl ${currentTier.bgColor} flex items-center justify-center`}>
                                            <TierIcon className="w-5 h-5" style={{ color: currentTier.color }} />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-semibold text-[#1D3D3D]">{currentTier.name} Plan</p>
                                            <p className="text-sm text-[#7D8E95]">Your subscription is now active</p>
                                        </div>
                                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#1D6B6B]/10 text-[#1D6B6B] text-sm font-medium">
                                            <Check className="w-4 h-4" />
                                            Active
                                        </span>
                                    </div>
                                </div>

                                {/* Features List */}
                                <div className="px-6 py-5">
                                    <p className="text-sm font-medium text-[#7D8E95] mb-4">You now have access to:</p>
                                    <ul className="space-y-3">
                                        {currentTier.features.map((feature, index) => (
                                            <li key={index} className="flex items-start gap-3">
                                                <div className="w-5 h-5 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                    <Check className="w-3 h-3 text-[#1D6B6B]" />
                                                </div>
                                                <span className="text-[#3D5A5A]">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Suggested Next Step */}
                                <div className="px-6 py-4 bg-[#1D6B6B]/5 border-t border-[#E8E1D6]">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-[#E08850]/20 flex items-center justify-center flex-shrink-0">
                                            <Star className="w-4 h-4 text-[#E08850]" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-[#1D3D3D]">Ready when you are</p>
                                            <p className="text-sm text-[#5A7A7A]">{currentTier.nextStep}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => navigate(currentTier.nextAction)}
                                    className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-[#1D6B6B] text-white font-semibold hover:bg-[#145555] transition-colors shadow-lg"
                                    data-testid="start-exploring-btn"
                                >
                                    <Sparkles className="w-5 h-5" />
                                    Begin Your Journey
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => navigate('/home')}
                                    className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-white border-2 border-[#E8E1D6] text-[#5A7A7A] font-semibold hover:border-[#1D6B6B] hover:text-[#1D6B6B] transition-colors"
                                    data-testid="go-to-dashboard-btn"
                                >
                                    <Heart className="w-5 h-5" />
                                    Go to Dashboard
                                </button>
                            </div>

                            {/* Support Note */}
                            <p className="text-center text-sm text-[#7D8E95] mt-8">
                                A receipt has been sent to your email. Questions? <button onClick={() => navigate('/help')} className="text-[#1D6B6B] hover:underline">Visit our FAQ</button>
                            </p>
                        </div>
                    )}

                    {/* Error State */}
                    {status === 'error' && (
                        <div className="text-center animate-fade-in">
                            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                                <XCircle className="w-10 h-10 text-red-500" />
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-[#1D3D3D] mb-3">
                                Something went wrong
                            </h1>
                            <p className="text-[#5A7A7A] mb-8 max-w-sm mx-auto">
                                We couldn't verify your payment. Don't worry — if you were charged, your subscription will be activated shortly.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto">
                                <button
                                    onClick={() => navigate('/home')}
                                    className="flex-1 px-6 py-3 rounded-xl bg-[#1D6B6B] text-white font-semibold hover:bg-[#145555] transition-colors"
                                    data-testid="go-to-dashboard-btn"
                                >
                                    Go to Dashboard
                                </button>
                                <button
                                    onClick={() => navigate('/pricing')}
                                    className="flex-1 px-6 py-3 rounded-xl bg-white border-2 border-[#E8E1D6] text-[#5A7A7A] font-semibold hover:border-[#1D6B6B] transition-colors"
                                    data-testid="try-again-btn"
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Expired State */}
                    {status === 'expired' && (
                        <div className="text-center animate-fade-in">
                            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#E08850]/10 flex items-center justify-center">
                                <Sparkles className="w-10 h-10 text-[#E08850]" />
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-[#1D3D3D] mb-3">
                                Session Expired
                            </h1>
                            <p className="text-[#5A7A7A] mb-8 max-w-sm mx-auto">
                                Your checkout session has expired. No charges were made. Would you like to try again?
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto">
                                <button
                                    onClick={() => navigate('/pricing')}
                                    className="flex-1 px-6 py-3 rounded-xl bg-[#1D6B6B] text-white font-semibold hover:bg-[#145555] transition-colors"
                                    data-testid="view-plans-btn"
                                >
                                    View Plans
                                </button>
                                <button
                                    onClick={() => navigate('/home')}
                                    className="flex-1 px-6 py-3 rounded-xl bg-white border-2 border-[#E8E1D6] text-[#5A7A7A] font-semibold hover:border-[#1D6B6B] transition-colors"
                                    data-testid="go-to-dashboard-btn"
                                >
                                    Go to Dashboard
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default PaymentSuccess;
